"use client";
import { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { CN_PRODUCT } from "@/utils/const";
import { PropsWithChildren } from "react";
import { curPostCodeOrAddress } from "@/common-components-src/js/utils/addressRequestUtils";
import { getAddressStore } from "@/common-components-src/js/address/AddressStore";

import ArrowIcon from "@/images/icon/arrow-default.svg";
import { queryPlatMind } from "@/apis/service";
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25217";
import { dataExpTagConfig } from "@/components/product/const";
// styles

import { AssuranceWrapper } from "@/styles/product";
import { ErrorBoundary } from "react-error-boundary";
import AgeVerification from "./verifyAge";

import { DialogContent, IconButton, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UKDialog from "@/common-components-src/components/UKDialog";
import AddressPostCodeList from "@/common-components-src/js/address/addressPostList";
import { useTranslation } from "@/i18n/client";

// 使用 observer 高阶组件包裹 Assurance 组件
const AssuranceWithObserver = observer(Assurance);

const Accordion = ({ noBottomBorder, children }: { noBottomBorder?: boolean } & PropsWithChildren) => {
  return <div className={`${CN_PRODUCT("sku-assurance")} ${noBottomBorder ? "noBottomBorder" : ""}`}>{children}</div>;
};
// styles

function Assurance({ floors, skuId }: any) {
  const { t } = useTranslation();
  const addressInstanceRef = useRef(null);
  const addressStore = getAddressStore();
  const postCode = addressStore.detailPostCode.get();
  const [isClient, setIsClient] = useState(false);
  const productShipPromiseFloor = floors.productShipPromiseFloor?.data;
  const guarantee = productShipPromiseFloor?.["C-M#viewAddressItem&guarantee"]?.items;

  const { promise, freightThresholdText } = productShipPromiseFloor?.["C-M#viewAddressItem&addressCore"] || {};
  // productComplianceFloor 烟酒年龄限制

  const productComplianceFloor = floors.productComplianceFloor?.data;

  const recycling = productShipPromiseFloor?.recycle;
  useEffect(() => {
    setIsClient(true);
  }, []);
  const [collapseActive, setCollapseActive] = useState<Record<string, boolean>>({
    weAccept: true,
  });
  const [weAccept, setWeAccept] = useState<any>();

  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const onGuaranteeClick = (index: number | string, text?: string) => {
    if (text) {
      sendClickTrack.Productdetail_BasicServiceFloor_Click({
        mainskuid: skuId,
        displayText: text,
      });
    }
    setCollapseActive({ ...collapseActive, [index]: !collapseActive[index] });
  };
  const initQueryPlatMind = async () => {
    const res = await queryPlatMind();
    setWeAccept(res);
  };

  useEffect(() => {
    initPostCode();
    initQueryPlatMind();
  }, []);

  const initPostCode = async () => {
    const postCode = await curPostCodeOrAddress();
    const detailPostCode = await curPostCodeOrAddress(true);
    addressStore.setPostCode(postCode);
    addressStore.setDetailPostCode(detailPostCode);
  };

  const handlerDeliver = () => {
    sendClickTrack.Productdetail_SZFloor_Click({
      mainskuid: skuId,
    });
    setIsOpenDialog(true);
  };

  const closeHandler = () => {
    setIsOpenDialog(false);
  };
  const addressDialog = () => {
    return (
      <>
        <UKDialog
          open={isOpenDialog}
          onClose={closeHandler}
          showCloseBtn={true}
          sx={{
            "& .MuiDialog-paper": {
              width: "880px", // 设置自定义宽度
              maxWidth: "none", // 取消最大宽度限制
              maxHeight: "534px",
              minHeight: "334px",
            },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={closeHandler}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <AddressPostCodeList handleAddressClose={closeHandler} isOpenDialog={isOpenDialog} />
          </DialogContent>
        </UKDialog>
      </>
    );
  };

  return (
    <AssuranceWrapper>
      <Accordion noBottomBorder={!guarantee?.length && !weAccept}>
        {/** 这里目前前端mock 固定值 */}
        <div
          className="accordion-detail deliver-frame"
          exptag={"exp|" + EXPOSURE_RECORD.Productdetail_SZFloor_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify({ mainskuid: skuId })}
        >
          <div className="update-postcode" onClick={handlerDeliver}>
            <p>
              <span className="bold">{postCode}</span>
            </p>
            <p className="arrow-right">
              <ArrowIcon />
            </p>
          </div>
          {promise && <div dangerouslySetInnerHTML={{ __html: promise }} />}
          {isClient && freightThresholdText && <div dangerouslySetInnerHTML={{ __html: freightThresholdText }} />}
        </div>
      </Accordion>
      {guarantee?.map((item: any, index: number) => {
        const styles = !collapseActive[index] ? { height: 0 } : {};
        const { logoUrl } = item || {};
        if (!item?.title) {
          return null;
        }
        return (
          <Accordion key={index}>
            {/* {JSON.stringify(item)} */}
            <div
              className={`accordion-summary ${!collapseActive[index] ? "expanded" : ""}`}
              onClick={() => onGuaranteeClick(index, item.title)}
              exptag={"exp|" + EXPOSURE_RECORD.Productdetail_BasicServiceFloor_Expo}
              data-exptag-config={dataExpTagConfig}
              data-exptag-json-param={JSON.stringify({ mainskuid: skuId, displayText: item.title })}
            >
              {/* {logoUrl && <CDNImage src={logoUrl} width={20} height={20} alt="icon" />} */}
              <p>{item.title}</p>
              <div className="arrow-wrapper">
                <ArrowIcon />
              </div>
            </div>
            <div className="accordion-detail" style={styles}>
              {isClient && <p className="content common-editor" dangerouslySetInnerHTML={{ __html: item.content }}></p>}
            </div>
          </Accordion>
        );
      })}

      {recycling && (
        <Accordion key="recycling">
          <div
            className={`accordion-summary ${!collapseActive["recycling"] ? "expanded" : ""}`}
            onClick={() => onGuaranteeClick("recycling", recycling.title)}
            exptag={"exp|" + EXPOSURE_RECORD.Productdetail_BasicServiceFloor_Expo}
            data-exptag-config={dataExpTagConfig}
            data-exptag-json-param={JSON.stringify({ mainskuid: skuId, displayText: recycling.title })}
          >
            {/* {logoUrl && <CDNImage src={logoUrl} width={20} height={20} alt="icon" />} */}
            <p>{recycling.title}</p>
            <div className="arrow-wrapper">
              <ArrowIcon />
            </div>
          </div>
          <div className="accordion-detail" style={!collapseActive["recycling"] ? { height: 0 } : {}}>
            {isClient && <p className="content common-editor" dangerouslySetInnerHTML={{ __html: recycling?.detailText }}></p>}
          </div>
        </Accordion>
      )}

      <AgeVerification data={productComplianceFloor} />
      <ErrorBoundary fallback={null}>
        {weAccept && (
          <>
            <div className={`accordion-summary ${!collapseActive["weAccept"] ? "expanded" : ""}`} onClick={() => onGuaranteeClick("weAccept")}>
              {/* {weAccept?.icon && <CDNImage src={weAccept?.icon} width={20} height={20} alt="icon" />} */}
              <p>{weAccept?.serviceTitle}</p>
              <div className="arrow-wrapper">
                <ArrowIcon />
              </div>
            </div>
            <div className="accordion-detail" style={!collapseActive["weAccept"] ? { height: 0 } : {}}>
              <p className="content" dangerouslySetInnerHTML={{ __html: weAccept?.serviceContent }}></p>
            </div>
          </>
        )}
      </ErrorBoundary>
      {addressDialog()}
    </AssuranceWrapper>
  );
}
export default AssuranceWithObserver; // 导出被 observer 包裹后的组件
//
